import { useState, useEffect } from "react"
import {
  Container,
  Typography,
  Box,
  Chip,
  Alert,
  AlertTitle,
  Card,
  CardContent,
  Button,
  Snackbar,
  CircularProgress,
} from "@mui/material"
import { 
  Handshake, 
  Share, 
  Percent, 
  Info,
  AccountBalance,
  Save,
  Edit
} from "@mui/icons-material"
import { updateCommissionData } from "../../../services/propertyService"
import { PropertyListing } from "../../../types/Property"

interface EditCommissionProps {
  selectedProperty: PropertyListing | null
}

export const EditCommissionPage = ({ selectedProperty }: EditCommissionProps) => {
  const [data, setData] = useState({ commission: "", commissionType: 1 })
  const [isLoading, setIsLoading] = useState(false)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error"
  })

  // Inicializar con datos de la propiedad seleccionada
  useEffect(() => {
    if (selectedProperty) {
      setData({
        commission: selectedProperty.commission || "",
        commissionType: selectedProperty.commisionType || 1
      })
    }
  }, [selectedProperty])

  const handleEdit = async () => {
    if (!selectedProperty?.id_Property) {
      setSnackbar({
        open: true,
        message: "No se ha seleccionado una propiedad válida",
        severity: "error"
      })
      return
    }

    setIsLoading(true)
    try {
      const response = await updateCommissionData(selectedProperty.id_Property, {commission: data.commission, comissionType: data.commissionType})
      
      if (response?.success) {
        selectedProperty.commission = data.commission;
        selectedProperty.commisionType = data.commissionType
        setSnackbar({
          open: true,
          message: response.message || "Comisión actualizada exitosamente",
          severity: "success"
        })
      } else {
        setSnackbar({
          open: true,
          message: response?.message || "Error al actualizar la comisión",
          severity: "error"
        })
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Error inesperado al actualizar la comisión",
        severity: "error"
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleCommissionTypeChange = (type: number) => {
    setData(prev => ({ ...prev, commissionType: type }))
  }

  const handleCommissionChange = (commissionValue: string) => {
    setData(prev => ({ ...prev, commission: commissionValue }))
  }

  const getCommissionTypeDescription = (type: number) => {
    switch (type) {
      case 1:
        return "Comisión directa: Haces un trato directo con el dueño de la propiedad para repartir el 100% de la comisión con otro broker."
      case 2:
        return "Comisión compartida: Un broker te está compartiendo su parte de la comisión que le fue asignada originalmente."
      default:
        return ""
    }
  }

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }))
  }

  if (!selectedProperty) {
    return (
      <Container maxWidth="md" sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <Alert severity="warning" sx={{ maxWidth: 400 }}>
          <AlertTitle>No hay propiedad seleccionada</AlertTitle>
          Selecciona una propiedad para editar su información de comisión.
        </Alert>
      </Container>
    )
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box textAlign="center" mb={5}>
        <Box 
          sx={{ 
            display: "inline-flex", 
            alignItems: "center", 
            justifyContent: "center",
            width: 60, 
            height: 60, 
            borderRadius: "50%", 
            background: "linear-gradient(135deg, #66bb6a, #4caf50)",
            mb: 2,
            boxShadow: "0 4px 20px rgba(76, 175, 80, 0.3)"
          }}
        >
          <Edit sx={{ fontSize: 30, color: "white" }} />
        </Box>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: "bold", color: "#2e7d32", fontSize: 25 }}>
          Editar Comisión
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary", maxWidth: 500, margin: "0 auto" }}>
          Modifica la información de comisión para la propiedad seleccionada.
        </Typography>
      </Box>

      {/* Información de la propiedad */}
      <Card sx={{ mb: 4, boxShadow: "0 2px 12px rgba(0,0,0,0.08)", borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <AccountBalance sx={{ color: "#4caf50", mr: 1 }} />
          </Box>
          <Typography variant="body2" color="text.secondary">
            <strong>Comisión actual:</strong> {selectedProperty.commission || "No especificada"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Tipo actual:</strong> {selectedProperty.commisionType === 1 ? "Comisión Directa" : "Comisión Compartida"}
          </Typography>
        </CardContent>
      </Card>

      {/* Tipo de Comisión */}
      <Card sx={{ mb: 4, boxShadow: "0 2px 12px rgba(0,0,0,0.08)", borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <Box 
              sx={{ 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center",
                width: 40, 
                height: 40, 
                borderRadius: "50%", 
                background: "linear-gradient(135deg, #81c784, #66bb6a)",
                mr: 2
              }}
            >
              <Handshake sx={{ fontSize: 24, color: "white" }} />
            </Box>
            <Typography variant="h6" sx={{ color: "#2e7d32", fontWeight: "bold" }}>
              Tipo de Comisión
            </Typography>
          </Box>
          
          <Box sx={{ display: "flex", gap: 3, mb: 3, flexWrap: "wrap", justifyContent: "center" }}>
            <Chip
              icon={<Handshake />}
              label="Comisión Directa"
              clickable
              onClick={() => handleCommissionTypeChange(1)}
              sx={{
                height: 48,
                backgroundColor: data.commissionType === 1 
                  ? "transparent"
                  : "rgba(76, 175, 80, 0.08)",
                background: data.commissionType === 1 
                  ? "linear-gradient(135deg, #66bb6a, #4caf50)" 
                  : "rgba(76, 175, 80, 0.08)",
                color: data.commissionType === 1 ? "white" : "#2e7d32",
                border: data.commissionType === 1 ? "none" : "1px solid #81c784",
                fontWeight: data.commissionType === 1 ? "bold" : "medium",
                fontSize: "0.95rem",
                px: 2,
                boxShadow: data.commissionType === 1 ? "0 4px 12px rgba(76, 175, 80, 0.3)" : "none",
                transition: "all 0.3s ease",
                "& .MuiChip-icon": {
                  color: data.commissionType === 1 ? "white" : "#4caf50"
                },
                "&:hover": {
                  transform: "translateY(-2px)",
                  background: data.commissionType === 1 
                    ? "linear-gradient(135deg, #5cb85c, #449d44)"
                    : "rgba(76, 175, 80, 0.15)",
                  boxShadow: "0 6px 16px rgba(76, 175, 80, 0.3)"
                }
              }}
            />
            
            <Chip
              icon={<Share />}
              label="Comisión Compartida"
              clickable
              onClick={() => handleCommissionTypeChange(2)}
              sx={{
                height: 48,
                backgroundColor: data.commissionType === 2 
                  ? "transparent"
                  : "rgba(76, 175, 80, 0.08)",
                background: data.commissionType === 2 
                  ? "linear-gradient(135deg, #66bb6a, #4caf50)" 
                  : "rgba(76, 175, 80, 0.08)",
                color: data.commissionType === 2 ? "white" : "#2e7d32",
                border: data.commissionType === 2 ? "none" : "1px solid #81c784",
                fontWeight: data.commissionType === 2 ? "bold" : "medium",
                fontSize: "0.95rem",
                px: 2,
                boxShadow: data.commissionType === 2 ? "0 4px 12px rgba(76, 175, 80, 0.3)" : "none",
                transition: "all 0.3s ease",
                "& .MuiChip-icon": {
                  color: data.commissionType === 2 ? "white" : "#4caf50"
                },
                "&:hover": {
                  transform: "translateY(-2px)",
                  background: data.commissionType === 2 
                    ? "linear-gradient(135deg, #5cb85c, #449d44)"
                    : "rgba(76, 175, 80, 0.15)",
                  boxShadow: "0 6px 16px rgba(76, 175, 80, 0.3)"
                }
              }}
            />
          </Box>

          {/* Descripción del tipo seleccionado */}
          <Alert 
            icon={<Info />}
            severity="info" 
            sx={{ 
              backgroundColor: "rgba(76, 175, 80, 0.08)",
              color: "#2e7d32",
              border: "1px solid rgba(129, 199, 132, 0.5)",
              borderRadius: 2,
              "& .MuiAlert-icon": {
                color: "#4caf50"
              }
            }}
          >
            <AlertTitle sx={{ color: "#2e7d32", fontWeight: "bold" }}>
              {data.commissionType === 1 ? "Comisión Directa" : "Comisión Compartida"}
            </AlertTitle>
            {getCommissionTypeDescription(data.commissionType)}
          </Alert>
        </CardContent>
      </Card>

      {/* Campo de Comisión */}
      <Card sx={{ mb: 4, boxShadow: "0 2px 12px rgba(0,0,0,0.08)", borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <Box 
              sx={{ 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center",
                width: 40, 
                height: 40, 
                borderRadius: "50%", 
                background: "linear-gradient(135deg, #81c784, #66bb6a)",
                mr: 2
              }}
            >
              <Percent sx={{ fontSize: 24, color: "white" }} />
            </Box>
            <Typography variant="h6" sx={{ color: "#2e7d32", fontWeight: "bold" }}>
              Términos de Comisión
            </Typography>
          </Box>
          
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
            Selecciona los términos de distribución de comisión que prefieres:
          </Typography>

          <Box sx={{ display: "flex", gap: 3, mb: 3, flexWrap: "wrap", justifyContent: "center" }}>
            <Chip
              icon={<Percent />}
              label="50/50"
              clickable
              onClick={() => handleCommissionChange("50/50")}
              sx={{
                height: 48,
                backgroundColor: data.commission === "50/50" 
                  ? "transparent"
                  : "rgba(76, 175, 80, 0.08)",
                background: data.commission === "50/50" 
                  ? "linear-gradient(135deg, #66bb6a, #4caf50)" 
                  : "rgba(76, 175, 80, 0.08)",
                color: data.commission === "50/50" ? "white" : "#2e7d32",
                border: data.commission === "50/50" ? "none" : "1px solid #81c784",
                fontWeight: data.commission === "50/50" ? "bold" : "medium",
                fontSize: "0.95rem",
                px: 2,
                boxShadow: data.commission === "50/50" ? "0 4px 12px rgba(76, 175, 80, 0.3)" : "none",
                transition: "all 0.3s ease",
                "& .MuiChip-icon": {
                  color: data.commission === "50/50" ? "white" : "#4caf50"
                },
                "&:hover": {
                  transform: "translateY(-2px)",
                  background: data.commission === "50/50" 
                    ? "linear-gradient(135deg, #5cb85c, #449d44)"
                    : "rgba(76, 175, 80, 0.15)",
                  boxShadow: "0 6px 16px rgba(76, 175, 80, 0.3)"
                }
              }}
            />
            
            <Chip
              icon={<Percent />}
              label="40/40/20"
              clickable
              onClick={() => handleCommissionChange("40/40/20")}
              sx={{
                height: 48,
                backgroundColor: data.commission === "40/40/20" 
                  ? "transparent"
                  : "rgba(76, 175, 80, 0.08)",
                background: data.commission === "40/40/20" 
                  ? "linear-gradient(135deg, #66bb6a, #4caf50)" 
                  : "rgba(76, 175, 80, 0.08)",
                color: data.commission === "40/40/20" ? "white" : "#2e7d32",
                border: data.commission === "40/40/20" ? "none" : "1px solid #81c784",
                fontWeight: data.commission === "40/40/20" ? "bold" : "medium",
                fontSize: "0.95rem",
                px: 2,
                boxShadow: data.commission === "40/40/20" ? "0 4px 12px rgba(76, 175, 80, 0.3)" : "none",
                transition: "all 0.3s ease",
                "& .MuiChip-icon": {
                  color: data.commission === "40/40/20" ? "white" : "#4caf50"
                },
                "&:hover": {
                  transform: "translateY(-2px)",
                  background: data.commission === "40/40/20" 
                    ? "linear-gradient(135deg, #5cb85c, #449d44)"
                    : "rgba(76, 175, 80, 0.15)",
                  boxShadow: "0 6px 16px rgba(76, 175, 80, 0.3)"
                }
              }}
            />
          </Box>

          {/* Descripción de la opción seleccionada */}
          {data.commission && (
            <Alert 
              icon={<Info />}
              severity="info" 
              sx={{ 
                backgroundColor: "rgba(76, 175, 80, 0.08)",
                color: "#2e7d32",
                border: "1px solid rgba(129, 199, 132, 0.5)",
                borderRadius: 2,
                "& .MuiAlert-icon": {
                  color: "#4caf50"
                }
              }}
            >
              <AlertTitle sx={{ color: "#2e7d32", fontWeight: "bold" }}>
                Distribución {data.commission}
              </AlertTitle>
              {data.commission === "50/50" 
                ? "Repartición equitativa entre dos partes: 50% para cada broker involucrado en la transacción."
                : "Distribución entre tres partes: dos brokers reciben 40% cada uno y un tercero recibe 20%."
              }
            </Alert>
          )}

          {!data.commission && (
            <Box 
              sx={{ 
                p: 2, 
                textAlign: "center", 
                color: "text.secondary",
                backgroundColor: "rgba(0, 0, 0, 0.02)",
                borderRadius: 2,
                border: "1px dashed #ccc"
              }}
            >
              <Percent sx={{ fontSize: 32, mb: 1, opacity: 0.5 }} />
              <Typography variant="body2">
                Selecciona una opción de distribución de comisión
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Botón de Guardar */}
      <Box display="flex" justifyContent="center" mt={4}>
        <Button
          variant="contained"
          size="large"
          onClick={handleEdit}
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <Save />}
          sx={{
            background: "linear-gradient(135deg, #66bb6a, #4caf50)",
            color: "white",
            px: 6,
            py: 1.5,
            borderRadius: 3,
            fontWeight: "bold",
            fontSize: "12px",
            boxShadow: "0 4px 16px rgba(76, 175, 80, 0.3)",
            "&:hover": {
              background: "linear-gradient(135deg, #5cb85c, #449d44)",
              boxShadow: "0 6px 20px rgba(76, 175, 80, 0.4)",
              transform: "translateY(-2px)"
            },
            "&:disabled": {
              background: "rgba(76, 175, 80, 0.3)",
              color: "rgba(255, 255, 255, 0.6)"
            },
            transition: "all 0.3s ease"
          }}
        >
          {isLoading ? "Guardando..." : "Guardar Cambios"}
        </Button>
      </Box>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ 
            width: '100%',
            "& .MuiAlert-icon": {
              color: snackbar.severity === "success" ? "#4caf50" : undefined
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}